import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { withJWTClient } from "./connectClient";
import type { ImportRequest } from "@/features/ListsV2/utils/importRequest";

import {
  deleteUsersResponseSchema,
  exportJobStatusSchema,
  exportResponseSchema,
  getListResponseSchema,
  importPreviewSchema,
  importResponseSchema,
  listImportJobSchema,
  paginatedListsResponseSchema,
  paginatedUsersResponseSchema,
  userListsResponseSchema,
  type DeleteUsersResponse,
  type ExportJobStatus,
  type ExportResponse,
  type GetListResponse,
  type ImportPreview,
  type ImportResponse,
  type List,
  type ListContactsSearch,
  type ListImportJob,
  type ListsSearch,
  type ListUpdate,
  type PaginatedListsResponse,
  type PaginatedUsersResponse,
  type UserListsLookup,
  type UserListsResponse,
} from "@/features/ListsV2/types";
import { isDemoUserFromStorage } from "@/utils/demoMode";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const API_BASE_URL: string = import.meta.env.DEV
  ? "/audiences"
  : (import.meta.env.VITE_AUDIENCES_API_URL as string) || "";

export const listsKeysV2 = {
  all: ["listsV2"] as const,
  lists: (siteId: string) => [...listsKeysV2.all, "lists", siteId] as const,
  listsWithParams: (siteId: string, params: ListsSearch) =>
    [...listsKeysV2.lists(siteId), params] as const,
  list: (siteId: string, listId: string) =>
    [...listsKeysV2.lists(siteId), listId] as const,
  stats: (siteId: string, listId: string) =>
    [...listsKeysV2.list(siteId, listId), "stats"] as const,
  users: (siteId: string, listId: string) =>
    [...listsKeysV2.list(siteId, listId), "users"] as const,
  usersWithParams: (
    siteId: string,
    listId: string,
    params: ListContactsSearch,
  ) => [...listsKeysV2.users(siteId, listId), params] as const,
  imports: (siteId: string) => [...listsKeysV2.all, "imports", siteId] as const,
  userLists: (siteId: string, lookup: UserListsLookup) =>
    [
      ...listsKeysV2.all,
      "userLists",
      siteId,
      lookup.kind,
      lookup.value,
    ] as const,
};

const SORT_FIELD_MAP: Record<NonNullable<ListsSearch["ordering"]>, string> = {
  createdAt: "SORT_FIELD_CREATED_AT",
  updatedAt: "SORT_FIELD_UPDATED_AT",
  name: "SORT_FIELD_NAME",
};

const SORT_ORDER_MAP: Record<NonNullable<ListsSearch["sortOrder"]>, string> = {
  asc: "SORT_ORDER_ASC",
  desc: "SORT_ORDER_DESC",
};

async function fetchLists(
  siteId: string,
  params: ListsSearch,
): Promise<PaginatedListsResponse> {
  return withJWTClient(async (client) => {
    const searchParams = new URLSearchParams();

    // Page-based pagination: pagination.page + pagination.limit
    if (params.page !== undefined)
      searchParams.set("pagination.page", String(params.page));
    if (params.pageSize !== undefined)
      searchParams.set("pagination.limit", String(params.pageSize));

    // Search filter on list name
    if (params.search) {
      searchParams.set("filter.condition.field", "FILTER_FIELD_NAME");
      searchParams.set("filter.condition.operator", "FILTER_OPERATOR_CONTAINS");
      searchParams.set("filter.condition.value.string_value", params.search);
    }
    // Sort
    if (params.ordering) {
      searchParams.set("sort.field", SORT_FIELD_MAP[params.ordering]);
      searchParams.set(
        "sort.order",
        SORT_ORDER_MAP[params.sortOrder ?? "desc"],
      );
    }

    const query = searchParams.toString();
    const url = `${API_BASE_URL}/${siteId}/lists${query ? `?${query}` : ""}`;
    const response = await client.get(url);
    return paginatedListsResponseSchema.parse(response.data);
  });
}

export function useListsV2(siteId: string, params: ListsSearch = {}) {
  return useQuery({
    queryKey: listsKeysV2.listsWithParams(siteId, params),
    queryFn: () => fetchLists(siteId, params),
    enabled: !!siteId,
  });
}

async function fetchAllLists(siteId: string): Promise<List[]> {
  const pageSize = 100;
  const allLists: List[] = [];
  let page = 1;
  for (;;) {
    const response = await fetchLists(siteId, { page, pageSize });
    allLists.push(...response.lists);
    if (response.lists.length < pageSize) break;
    page += 1;
  }
  return allLists;
}

export const listsArrayQueryOptions = (siteId: string) => ({
  queryKey: [...listsKeysV2.lists(siteId), "all"],
  queryFn: () => fetchAllLists(siteId),
  enabled: !!siteId,
  staleTime: 30_000,
});

export function useCreateListV2(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string }) =>
      withJWTClient((client) =>
        client.post(`${API_BASE_URL}/${siteId}/lists`, data),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listsKeysV2.lists(siteId),
      });
    },
  });
}

async function fetchList(
  siteId: string,
  listId: string,
): Promise<GetListResponse> {
  return withJWTClient(async (client) => {
    const url = `${API_BASE_URL}/${siteId}/lists/${listId}`;
    const response = await client.get(url);
    return getListResponseSchema.parse(response.data);
  });
}

export function useListV2(siteId: string, listId: string) {
  return useQuery({
    queryKey: listsKeysV2.list(siteId, listId),
    queryFn: () => fetchList(siteId, listId),
    enabled: !!siteId && !!listId,
  });
}

export function useUpdateListV2(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: ListUpdate }) =>
      withJWTClient((client) =>
        client.put(`${API_BASE_URL}/${siteId}/lists/${listId}`, data),
      ),
    onSuccess: (_data, { listId }) => {
      void queryClient.invalidateQueries({
        queryKey: listsKeysV2.list(siteId, listId),
      });
    },
  });
}

export function useDeleteListV2(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (listId: string) =>
      withJWTClient((client) =>
        client.delete(`${API_BASE_URL}/${siteId}/lists/${listId}`),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listsKeysV2.lists(siteId),
      });
    },
  });
}

const CONTACTS_SORT_FIELD_MAP: Record<
  NonNullable<ListContactsSearch["ordering"]>,
  string
> = {
  createdAt: "SORT_FIELD_CREATED_AT",
  updatedAt: "SORT_FIELD_UPDATED_AT",
  email: "SORT_FIELD_EMAIL",
  phone: "SORT_FIELD_PHONE",
  firstName: "SORT_FIELD_FIRST_NAME",
  lastName: "SORT_FIELD_LAST_NAME",
};

async function fetchListUsers(
  siteId: string,
  listId: string,
  params: ListContactsSearch = {},
): Promise<PaginatedUsersResponse> {
  return withJWTClient(async (client) => {
    const searchParams = new URLSearchParams();

    if (params.page !== undefined)
      searchParams.set("pagination.page", String(params.page));
    if (params.pageSize !== undefined)
      searchParams.set("pagination.limit", String(params.pageSize));

    if (params.search) {
      searchParams.set("filter.condition.field", "FILTER_FIELD_EMAIL");
      searchParams.set("filter.condition.operator", "FILTER_OPERATOR_CONTAINS");
      searchParams.set("filter.condition.value.string_value", params.search);
    }

    if (params.ordering) {
      searchParams.set("sort.field", CONTACTS_SORT_FIELD_MAP[params.ordering]);
      searchParams.set(
        "sort.order",
        SORT_ORDER_MAP[params.sortOrder ?? "desc"],
      );
    }

    const query = searchParams.toString();
    const url = `${API_BASE_URL}/${siteId}/lists/${listId}/users${query ? `?${query}` : ""}`;
    const response = await client.get(url);
    return paginatedUsersResponseSchema.parse(response.data);
  });
}

export function useListUsersV2(
  siteId: string,
  listId: string,
  params: ListContactsSearch = {},
) {
  return useQuery({
    queryKey: listsKeysV2.usersWithParams(siteId, listId, params),
    queryFn: () => fetchListUsers(siteId, listId, params),
    enabled: !!siteId && !!listId,
  });
}

export function useDeleteContactsV2(siteId: string, listId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string[]): Promise<DeleteUsersResponse> =>
      withJWTClient(async (client) => {
        const response = await client.delete(
          `${API_BASE_URL}/${siteId}/lists/${listId}/users`,
          { data: { userIds } },
        );
        return deleteUsersResponseSchema.parse(response.data);
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listsKeysV2.users(siteId, listId),
      });
    },
  });
}

export function useExportListV2(siteId: string, listId: string) {
  return useMutation({
    mutationFn: (fields: string[]): Promise<ExportResponse> =>
      withJWTClient(async (client) => {
        const response = await client.post(
          `${API_BASE_URL}/${siteId}/lists/${listId}/export`,
          { export_metadata: { format: "CSV", fields } },
        );
        return exportResponseSchema.parse(response.data);
      }),
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to export list");
    },
  });
}

export async function fetchExportJob(
  siteId: string,
  jobId: string,
): Promise<ExportJobStatus> {
  return withJWTClient(async (client) => {
    const response = await client.get(
      `${API_BASE_URL}/${siteId}/jobs/${jobId}`,
    );
    return exportJobStatusSchema.parse(response.data);
  });
}

function importFormData({ file, metadata }: ImportRequest): FormData {
  const formData = new FormData();
  // metadata must precede file: the server reads the multipart body in
  // order and needs the field mapping before it can validate the CSV.
  formData.append("metadata", JSON.stringify(metadata));
  formData.append("file", file);
  return formData;
}

export function useImportListV2(siteId: string, listId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ImportRequest): Promise<ImportResponse> =>
      withJWTClient(async (client) => {
        const response = await client.post(
          `${API_BASE_URL}/${siteId}/lists/${listId}/import`,
          importFormData(data),
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        return importResponseSchema.parse(response.data);
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listsKeysV2.users(siteId, listId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to import contacts");
    },
  });
}

export function previewImportV2(
  siteId: string,
  listId: string,
  request: ImportRequest,
  signal?: AbortSignal,
): Promise<ImportPreview> {
  return withJWTClient(async (client) => {
    const response = await client.post(
      `${API_BASE_URL}/${siteId}/lists/${listId}/import:preview`,
      importFormData(request),
      { headers: { "Content-Type": "multipart/form-data" }, signal },
    );
    return importPreviewSchema.parse(response.data);
  });
}

// Keyed outside listsKeysV2.lists(siteId): finishing an import invalidates that
// prefix, and a refetch here would upload the file again.
export function useImportPreviewV2(
  siteId: string,
  listId: string,
  request: ImportRequest | undefined,
  fingerprint: string,
) {
  // Demo users cannot POST, and blocking one would raise a snackbar.
  const canPreview = request != null && !isDemoUserFromStorage();

  return useQuery<ImportPreview>({
    queryKey: ["listsV2ImportPreview", siteId, listId, fingerprint],
    queryFn: canPreview
      ? ({ signal }) => previewImportV2(siteId, listId, request, signal)
      : skipToken,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useImportJobStatusV2(siteId: string, jobId: string | null) {
  return useQuery<ListImportJob>({
    queryKey: [...listsKeysV2.imports(siteId), jobId],
    queryFn: () =>
      withJWTClient(async (client) => {
        const response = await client.get(
          `${API_BASE_URL}/${siteId}/jobs/${jobId}`,
        );
        return listImportJobSchema.parse(response.data);
      }),
    enabled: !!siteId && !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      if (status === "JOB_STATUS_PENDING" || status === "JOB_STATUS_RUNNING") {
        return 5000;
      }
      return false;
    },
  });
}

export function useCancelJobV2(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) =>
      withJWTClient(async (client) => {
        await client.post(`${API_BASE_URL}/${siteId}/jobs/${jobId}/cancel`);
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listsKeysV2.imports(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to cancel import");
    },
  });
}

async function fetchUserLists(
  siteId: string,
  lookup: UserListsLookup,
): Promise<UserListsResponse> {
  return withJWTClient<UserListsResponse>(async (client) => {
    const params = new URLSearchParams();
    if (lookup.kind === "email") params.set("email", lookup.value);
    if (lookup.kind === "phone") params.set("phone", lookup.value);
    if (lookup.kind === "userId") params.set("user_id", lookup.value);
    const qs = params.toString();
    const url = `${API_BASE_URL}/${siteId}/users/lists${qs ? `?${qs}` : ""}`;
    const response = await client.get(url);
    return userListsResponseSchema.parse(response.data);
  });
}

export function useUserListsV2(
  siteId: string,
  lookup: UserListsLookup,
): UseQueryResult<UserListsResponse, Error> {
  return useQuery<UserListsResponse, Error>({
    queryKey: listsKeysV2.userLists(siteId, lookup),
    queryFn: () => fetchUserLists(siteId, lookup),
    enabled: !!siteId && !!lookup.value.trim(),
  });
}
